import React, { useEffect, useState } from "react";
import serviceApi from "../../../services/serviceApi";
import environment from "../../../environments/environment";
import ExamSchedule from "./ExamSchedule";
import PTMSchedule from "./PTMSchedule";

type DayItem = {
    date: string;
    day: string;
    dayOfMonth: number;
    events: any[];
    exams: any[];
    holidays: any[];
    absent: boolean;
};

const academicMonths = [
    { name: "MAY", api: "may", jsMonth: 4 },
    { name: "JUN", api: "june", jsMonth: 5 },
    { name: "JUL", api: "july", jsMonth: 6 },
    { name: "AUG", api: "august", jsMonth: 7 },
    { name: "SEP", api: "september", jsMonth: 8 },
    { name: "OCT", api: "october", jsMonth: 9 },
    { name: "NOV", api: "november", jsMonth: 10 },
    { name: "DEC", api: "december", jsMonth: 11 },
    { name: "JAN", api: "january", jsMonth: 0 },
    { name: "FEB", api: "february", jsMonth: 1 },
    { name: "MAR", api: "march", jsMonth: 2 },
    { name: "APR", api: "april", jsMonth: 3 }
];

const weekDays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

interface CalendarProps {
    monthIndex?: number;
}

const Calendar: React.FC<CalendarProps> = ({ monthIndex }) => {
    const getInitialMonthIndex = () => {
        if (monthIndex !== undefined) return monthIndex;
        const currentJSMonth = new Date().getMonth();
        const foundIndex = academicMonths.findIndex(m => m.jsMonth === currentJSMonth);
        return foundIndex !== -1 ? foundIndex : 0;
    };

    const [isLoading, setIsLoading] = useState(false);
    const [selectedMonth, setSelectedMonth] = useState(getInitialMonthIndex);
    const [days, setDays] = useState<DayItem[]>([]);
    const [selectedDay, setSelectedDay] = useState<DayItem | null>(null);
    const [emptyDays, setEmptyDays] = useState<number[]>([]);
    const [showAbsents, setShowAbsents] = useState(false);
    const [showSchedules, setShowSchedules] = useState(false);
    const [absentList, setAbsentList] = useState<DayItem[]>([]);
    const [monthActivities, setMonthActivities] = useState({
        events: [] as any[],
        exams: [] as any[],
        holidays: [] as any[]
    });
    const [selectedType, setSelectedType] = useState<"event" | "exam" | "holiday" | null>(null);
    const [selectedExam, setSelectedExam] = useState<any>(null);
    const [selectedPtm, setSelectedPtm] = useState<any>(null);

    const getDisplayYear = (mIdx: number) => {
        const now = new Date();
        const currentMonth = now.getMonth();
        const currentYear = now.getFullYear();
        let anchorYear = currentYear;
        if (currentMonth <= 3) anchorYear--;
        return mIdx >= 8 ? anchorYear + 1 : anchorYear;
    };

    useEffect(() => {
        if (monthIndex !== undefined) {
            setSelectedMonth(monthIndex);
        }
    }, [monthIndex]);

    useEffect(() => {
        fetchMonthData(selectedMonth);
    }, [selectedMonth]);

    const fetchMonthData = async (mIdx: number) => {
        setIsLoading(true);
        try {
            const monthObj = academicMonths[mIdx];
            const res = await serviceApi.get(
                `${environment.calendar}?date=${monthObj.api}`
            );
            if (res?.status === "SUCCESS") {
                const list: DayItem[] = res.dataList || [];
                setDays(list);
                if (list.length > 0) {
                    const firstDate = new Date(list[0].date);
                    const firstDay = firstDate.getDay();
                    setEmptyDays(Array(firstDay).fill(0));
                } else {
                    setEmptyDays([]);
                }
                const today = new Date().getDate();
                let selected =
                    list.find((d) => d.dayOfMonth === today) ||
                    list.find(
                        (d) =>
                            d.events?.length > 0 ||
                            d.exams?.length > 0 ||
                            d.holidays?.length > 0
                    ) ||
                    list[0];
                setSelectedDay(selected || null);
                if (selected?.events?.length) setSelectedType("event");
                else if (selected?.exams?.length) setSelectedType("exam");
                else if (selected?.holidays?.length) setSelectedType("holiday");
                else setSelectedType(null);

                setAbsentList(list.filter((d) => d.absent));

                const events: any[] = [];
                const exams: any[] = [];
                const holidays: any[] = [];

                list.forEach((d) => {
                    d.events?.forEach((e) => events.push(e));
                    d.exams?.forEach((e) => exams.push(e));
                    d.holidays?.forEach((h) => holidays.push(h));
                });
                setMonthActivities({ events, exams, holidays });
            }
        } finally {
            setIsLoading(false);
        }
    };

    const selectDay = (day: DayItem) => {
        setSelectedDay(day);
        if (day.events?.length) setSelectedType("event");
        else if (day.exams?.length) setSelectedType("exam");
        else if (day.holidays?.length) setSelectedType("holiday");
        else setSelectedType(null);
    };

    const renderContentCard = () => {
        if (!selectedDay) return null;

        const cardStyle = {
            background: "#fff",
            borderRadius: 12,
            padding: 14,
            marginBottom: 12,
            boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
            borderLeft: "5px solid",
            position: "relative",
            paddingRight: "30px"
        } as React.CSSProperties;

        const titleStyle = {
            fontWeight: 700,
            marginBottom: 6,
            fontSize: 15
        } as React.CSSProperties;

        return (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {selectedDay.events
                    ?.filter(e => e.type === "PTM")
                    .map((e, i) => (
                        <div
                            key={`ptm-${i}`}
                            onClick={() => setSelectedPtm(e)}
                            style={{
                                ...cardStyle,
                                borderLeftColor: "green",
                                cursor: "pointer"
                            }}
                        >
                            <div style={{ ...titleStyle, color: "green" }}>EVENT</div>
                            <i
                                className="fa fa-chevron-right"
                                style={{
                                    color: "#666",
                                    fontSize: "14px",
                                    position: "absolute",
                                    right: "14px",
                                    top: "50%",
                                    transform: "translateY(-50%)"
                                }}
                            />
                            <div style={{ fontSize: 14, fontWeight: 500 }}>
                                {e.title}
                            </div>
                            {e.date && (
                                <div style={{ fontSize: 12, color: "#777" }}>
                                    {e.date}
                                </div>
                            )}
                        </div>
                    ))}
                {selectedDay.exams?.map((e, i) => (
                    <div
                        key={`exam-card-${i}`}
                        onClick={() => setSelectedExam(e)} // Passes the specific clicked exam instead of just the first one [0]
                        style={{ ...cardStyle, borderLeftColor: "#FF8800", cursor: "pointer" }}
                    >
                        <div style={{ ...titleStyle, color: "#FF8800" }}>EXAM</div>
                        <i
                            className="fa fa-chevron-right"
                            style={{ color: "#666", fontSize: "14px", position: "absolute", right: "14px", top: "50%", transform: "translateY(-50%)" }}
                        />
                        <div style={{ fontSize: 14, fontWeight: 500 }}>{e.title}</div>
                        {e.examtype && (
                            <div style={{ fontSize: 12, color: "#777", marginTop: 3 }}>
                                {e.examtype}
                            </div>
                        )}
                    </div>
                ))}
                {selectedDay.holidays?.map((h, i) => (
                    <div
                        key={`holiday-card-${i}`}
                        style={{ ...cardStyle, borderLeftColor: "red" }}
                    >
                        <div style={{ ...titleStyle, color: "red" }}>HOLIDAY</div>
                        <div style={{ fontSize: 14, fontWeight: 500 }}>
                            {h.event || h.title}
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    const toggleIcon = (v: boolean) => (
        <i className={`fa ${v ? "fa-chevron-up" : "fa-chevron-down"}`} />
    );

    if (selectedExam) {
        return (
            <ExamSchedule
                entityId={selectedExam.title}
                onBack={() => setSelectedExam(null)}
                calendarMode={true}
            />
        );
    }

    if (selectedPtm) {
        return (
            <PTMSchedule
                entityId={selectedPtm.title}
                onBack={() => setSelectedPtm(null)}
            />
        );
    }

    return (
        <div style={{ position: "relative", border: "1px solid #ddd", borderRadius: "12px", padding: "10px", background: "#f9f9f9", marginTop: "40px" }}>
            {isLoading && (
                <div className="loading-overlay" style={{ position: "absolute", zIndex: 10 }}>
                    <div className="loading-overlay__content">
                        <div className="spinner spinner--lg"></div>
                        <span className="loading-text">Loading...</span>
                    </div>
                </div>
            )}

            {monthIndex === undefined && (
                <div
                    style={{
                        display: "flex",
                        gap: 8,
                        padding: 10,
                        overflowX: "auto",
                        whiteSpace: "nowrap",
                        scrollbarWidth: "none",
                    }}
                    className="calendar-month-row"
                >
                    {academicMonths.map((m, i) => (
                        <button
                            key={m.name}
                            onClick={() => setSelectedMonth(i)}
                            style={{
                                minWidth: 90,
                                padding: 10,
                                borderRadius: 20,
                                border: "none",
                                fontWeight: 700,
                                background: selectedMonth === i ? "#1976d2" : "#eee",
                                color: selectedMonth === i ? "#fff" : "#333"
                            }}>
                            {m.name}
                        </button>
                    ))}
                </div>
            )}
            <div style={{ display: "flex", flexDirection: "row", flexWrap: "wrap", gap: "20px", padding: "5px" }}>
                <div style={{ flex: "1 1 300px", minWidth: "260px" }}>
                    <div style={{ textAlign: "center", fontWeight: 700, marginBottom: 10 }}>
                        {academicMonths[selectedMonth].name} {getDisplayYear(selectedMonth)}
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", textAlign: "center", fontSize: 12, fontWeight: 600, marginBottom: 5 }}>
                        {weekDays.map((d) => <div key={d}>{d}</div>)}
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 5 }}>
                        {emptyDays.map((_, i) => <div key={i} />)}
                        {days.map((day, i) => (
                            <div
                                key={i}
                                onClick={() => selectDay(day)}
                                style={{
                                    height: 50,
                                    position: "relative",
                                    overflow: "hidden",
                                    borderRadius: 8,
                                    border:
                                        selectedDay?.dayOfMonth === day.dayOfMonth
                                            ? day.absent
                                                ? "2px solid #ff0000"
                                                : "2px solid #1976d2"
                                            : "1px solid #eee",
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    cursor: "pointer",
                                    background: "#fff"
                                }}>
                                <div style={{ fontSize: 13, fontWeight: 700 }}>{day.dayOfMonth}</div>
                                {(() => {
                                    const hasEvent = day.events?.length > 0;
                                    const hasExam = day.exams?.length > 0;
                                    const hasHoliday = day.holidays?.length > 0;
                                    const isAbsent = day.absent;

                                    return (
                                        <>
                                            <div
                                                style={{
                                                    display: "flex",
                                                    gap: 3,
                                                    marginTop: 2,
                                                    alignItems: "center"
                                                }}
                                            >
                                                {hasEvent && (
                                                    <div
                                                        style={{
                                                            width: 5,
                                                            height: 5,
                                                            borderRadius: "50%",
                                                            background: "green"
                                                        }}
                                                    />
                                                )}
                                                {hasExam && (
                                                    <div
                                                        style={{
                                                            width: 5,
                                                            height: 5,
                                                            borderRadius: "50%",
                                                            background: "#FF8800"
                                                        }}
                                                    />
                                                )}
                                                {hasHoliday && (
                                                    <div
                                                        style={{
                                                            width: 5,
                                                            height: 5,
                                                            borderRadius: "50%",
                                                            background: "red"
                                                        }}
                                                    />
                                                )}
                                            </div>
                                            {isAbsent && (
                                                <div
                                                    style={{
                                                        position: "absolute",
                                                        bottom: 0,
                                                        left: 0,
                                                        right: 0,
                                                        height: 3,
                                                        background: "#ff0000",
                                                        zIndex: 3
                                                    }}
                                                />
                                            )}
                                        </>
                                    );
                                })()}
                            </div>
                        ))}
                    </div>
                </div>
                <div
                    style={{
                        width: "100%",
                        marginTop: 20
                    }}
                >
                    <div style={{ marginBottom: 10 }}>
                        {renderContentCard()}
                    </div>
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            padding: "5px 0",
                            color: "#ff4d4f",
                            cursor: "pointer",
                            fontWeight: 600,
                            fontSize: 13,
                            marginBottom: 10
                        }}
                    >
                        <div onClick={() => setShowAbsents(!showAbsents)}>
                            Missed {toggleIcon(showAbsents)}
                        </div>

                        <div onClick={() => setShowSchedules(!showSchedules)}>
                            Schedules {toggleIcon(showSchedules)}
                        </div>
                    </div>

                    {showAbsents && (
                        <div
                            style={{
                                marginBottom: 12,
                                background: "#fff",
                                borderRadius: 12,
                                padding: 10,
                                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                                borderLeft: "5px solid #ff4d4f"
                            }}
                        >
                            {absentList.length === 0 ? (
                                <div style={{ color: "#777", fontSize: 12 }}>
                                    No missed sessions
                                </div>
                            ) : (
                                absentList.map((a, i) => (
                                    <div
                                        key={i}
                                        style={{
                                            padding: "5px 0",
                                            fontSize: 12,
                                            fontWeight: 500
                                        }}
                                    >
                                        {a.date}
                                    </div>
                                ))
                            )}
                        </div>
                    )}

                    {showSchedules && (
                        <div
                            style={{
                                marginBottom: 12,
                                background: "#fff",
                                borderRadius: 12,
                                padding: 10,
                                boxShadow: "0 2px 8px rgba(0,0,0,0.08)"
                            }}
                        >
                            {monthActivities.events.length === 0 &&
                                monthActivities.exams.length === 0 &&
                                monthActivities.holidays.length === 0 ? (
                                <div style={{ color: "#777", fontSize: 12 }}>
                                    No schedules available
                                </div>
                            ) : (
                                <>
                                    {/* EVENTS */}
                                    {Array.from(new Map(monthActivities.events.map(e => [`${e.title}-${e.date}`, e])).values()).map((e: any, i, arr) => (
                                        <div
                                            key={`event-${i}`}
                                            style={{
                                                padding: "8px 0",
                                                fontSize: 12,
                                                borderBottom: i === arr.length - 1 ? "none" : "2px solid #e0e0e0"
                                            }}
                                        >
                                            {i === 0 && (
                                                <div
                                                    style={{
                                                        background: "green",
                                                        color: "#fff",
                                                        fontSize: 10,
                                                        fontWeight: 700,
                                                        padding: "2px 6px",
                                                        borderRadius: 10,
                                                        display: "inline-block",
                                                        marginBottom: 6
                                                    }}
                                                >
                                                    EVENTS
                                                </div>
                                            )}

                                            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                                <span style={{ fontWeight: 600 }}>{e.title}</span>
                                                <span style={{ color: "#666", fontSize: 11 }}>{e.date}</span>
                                            </div>
                                        </div>
                                    ))}

                                    {/* EXAMS */}
                                    {Array.from(new Map(monthActivities.exams.map(e => [`${e.title}-${e.startdate}`, e])).values()).map((e: any, i, arr) => (
                                        <div
                                            key={`exam-${i}`}
                                            style={{
                                                padding: "8px 0",
                                                fontSize: 12,
                                                borderBottom: i === arr.length - 1 ? "none" : "2px solid #e0e0e0"
                                            }}
                                        >
                                            {i === 0 && (
                                                <div
                                                    style={{
                                                        background: "#FF8800",
                                                        color: "#fff",
                                                        fontSize: 10,
                                                        fontWeight: 700,
                                                        padding: "2px 6px",
                                                        borderRadius: 10,
                                                        display: "inline-block",
                                                        marginTop: 6,
                                                        marginBottom: 6
                                                    }}
                                                >
                                                    EXAMS
                                                </div>
                                            )}

                                            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                                <span style={{ fontWeight: 600 }}>{e.title}</span>
                                                <span style={{ color: "#666", fontSize: 11 }}>
                                                    {e.startdate}
                                                    {e.enddate && e.enddate !== e.startdate ? ` - ${e.enddate}` : ""}
                                                </span>
                                            </div>
                                        </div>
                                    ))}

                                    {/* HOLIDAYS */}
                                    {Array.from(new Map(monthActivities.holidays.map(h => [`${h.event || h.title}-${h.evedate}`, h])).values()).map((h: any, i, arr) => (
                                        <div
                                            key={`holiday-${i}`}
                                            style={{
                                                padding: "8px 0",
                                                fontSize: 12,
                                                borderBottom: i === arr.length - 1 ? "none" : "2px solid #e0e0e0"
                                            }}
                                        >
                                            {i === 0 && (
                                                <div
                                                    style={{
                                                        background: "red",
                                                        color: "#fff",
                                                        fontSize: 10,
                                                        fontWeight: 700,
                                                        padding: "2px 6px",
                                                        borderRadius: 10,
                                                        display: "inline-block",
                                                        marginTop: 6,
                                                        marginBottom: 6
                                                    }}
                                                >
                                                    HOLIDAYS
                                                </div>
                                            )}

                                            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                                <span style={{ fontWeight: 600 }}>{h.event || h.title}</span>
                                                <span style={{ color: "#666", fontSize: 11 }}>{h.evedate}</span>
                                            </div>
                                        </div>
                                    ))}
                                </>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Calendar;